import {getTrustTypeStrategy} from '@sollapay/domain/trust-types';
import {createContext, useContext, useMemo} from 'react';

import {DealTypeProvider} from '@/lib/i18n';

import {resolveTrustAccountPermissions} from '../utils';

import type {TrustTypeStrategy} from '@sollapay/domain/trust-types';
import type {GeneralSaleTransactionType, TrustPurpose} from '@sollapay/enums';
import type {TrustAccountDTO, TrustAccountSkeletonDraftDTO} from '@sollapay/types';
import type {FC, ReactNode} from 'react';

/**
 * The single trust account the surrounding page is scoped to. Picked from the DTO rather
 * than redeclared so callers can hand their loaded account straight in, with no remapping.
 * `trustPurpose` is normalized to `TrustPurpose | null` (the domain convention) rather than
 * inheriting the DTO's optional-undefined shape.
 */
export type CurrentTrustAccount = Pick<
  TrustAccountSkeletonDraftDTO,
  'id' | 'lawyerId' | 'lawyerName' | 'refId' | 'name' | 'trustStatus'
> & {
  trustPurpose: TrustPurpose | null;
  generalSaleTransactionType: GeneralSaleTransactionType | null;
  isTrustee: boolean;
};

/**
 * Write permissions for this account, read from the actions the API says the caller may perform
 * here — the firm role, its scopes and the caller's account role are all already folded in.
 */
export interface TrustAccountPermissions {
  canUpdateAccount: boolean;
  canSignDeclarations: boolean;
  canApproveBuyerIdentityChange: boolean;
  canSubmitForReview: boolean;
  canCreateBuyer: boolean;
  canUpdateBuyer: boolean;
  canDeleteBuyer: boolean;
  canCreateBeneficiary: boolean;
  canUpdateBeneficiary: boolean;
  canDeleteBeneficiary: boolean;
  canCollectPayments: boolean;
  canCreatePayout: boolean;
  canResendPayments: boolean;
  canUploadDocuments: boolean;
  canDeleteDocuments: boolean;
  canManageMembers: boolean;
}

export interface TrustAccountContextValue {
  account: CurrentTrustAccount;
  permissions: TrustAccountPermissions;
}

/** Exported for the Storybook decorator to seed directly — app code uses the hooks below. */
export const TrustAccountContext = createContext<TrustAccountContextValue | null>(null);

export interface TrustAccountProviderProps {
  /**
   * Must be fully loaded. Accepts the DTO's optional `trustPurpose` directly — normalized to
   * `null` for the context below.
   */
  account: Pick<
    TrustAccountDTO['trustAccount'],
    | 'id'
    | 'lawyerId'
    | 'lawyerName'
    | 'refId'
    | 'name'
    | 'trustStatus'
    | 'trustPurpose'
    | 'generalSaleTransactionType'
    | 'isTrustee'
    | 'allowedActions'
  >;
  children: ReactNode;
}

export const TrustAccountProvider: FC<TrustAccountProviderProps> = ({account, children}) => {
  const {
    id,
    lawyerId,
    lawyerName,
    refId,
    name,
    trustStatus,
    trustPurpose,
    generalSaleTransactionType,
    isTrustee,
    allowedActions
  } = account;

  // Depends on the individual fields rather than `account` so a caller passing a freshly
  // built object doesn't invalidate this on every render.
  const value = useMemo<TrustAccountContextValue>(
    () => ({
      account: {
        id,
        lawyerId,
        lawyerName,
        refId,
        name,
        trustStatus,
        trustPurpose: trustPurpose ?? null,
        generalSaleTransactionType: generalSaleTransactionType ?? null,
        isTrustee
      },
      permissions: resolveTrustAccountPermissions(allowedActions)
    }),
    [
      id,
      lawyerId,
      lawyerName,
      refId,
      name,
      trustStatus,
      trustPurpose,
      generalSaleTransactionType,
      isTrustee,
      allowedActions
    ]
  );

  return (
    <TrustAccountContext.Provider value={value}>
      <DealTypeProvider
        purpose={trustPurpose ?? null}
        transactionType={generalSaleTransactionType ?? null}
      >
        {children}
      </DealTypeProvider>
    </TrustAccountContext.Provider>
  );
};

function useTrustAccountContext(hookName: string): TrustAccountContextValue {
  const context = useContext(TrustAccountContext);
  if (!context) {
    throw new Error(`${hookName} must be used within a TrustAccountProvider`);
  }
  return context;
}

export function useCurrentTrustAccount(): CurrentTrustAccount {
  return useTrustAccountContext('useCurrentTrustAccount').account;
}

/** Resolves the current account's deal-type strategy once, instead of every call site asking. */
export function useTrustTypeStrategy(): TrustTypeStrategy {
  const {trustPurpose, generalSaleTransactionType} = useCurrentTrustAccount();
  return useMemo(
    () => getTrustTypeStrategy({trustPurpose, generalSaleTransactionType}),
    [trustPurpose, generalSaleTransactionType]
  );
}

export function useTrustAccountPermissions(): TrustAccountPermissions {
  return useTrustAccountContext('useTrustAccountPermissions').permissions;
}

export function useCanWriteAnything(): boolean {
  const permissions = useTrustAccountPermissions();
  return Object.values(permissions).some(Boolean);
}
